import React, { useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import { EmailFrame } from '../../../../marketing-v2/features/email-composer/components/EmailHtml'
import { marketingApiService } from '../../../../marketing-v2/services/marketingApiService'
import { hapticTick, hapticSuccess, hapticError } from '../../../../../_utils/haptics'
import { drawEmail, emailDraft } from '../makeThings'
import { useBrand, useRecall } from '../useBeu'
import { Tick, Mail, Palette } from './icons'
import { useSaver } from '../saveBus'

export const CAMPAIGNS_PATH = '/marketing-campaigns'
export const BRAND_PATH = '/marketing-brand'

// An email Bailey wrote, drawn in the salon's own template and brand. Saving
// makes a draft in Campaigns and nothing more: the salon picks who gets it
// and sends it from there.
const EmailWidget = ({ block, id, compact, go, asked = '', latest = false }) => {
  const brand = useBrand()
  const dispatch = useDispatch()
  const [memo, setMemo] = useRecall(id)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)
  const [whole, setWhole] = useState(false)
  const campaignId = (memo && memo.campaignId) || block.draftId || null
  const hasWords = !!(block.subject || block.heading || (block.body && block.body.length))
  const drawn = useMemo(() => {
    if (!hasWords) return null
    try { return drawEmail(block, { market: brand.market, kit: brand.spec, links: brand.links, salonName: brand.salonName, asked }) } catch (e) { return null }
  }, [id, hasWords, brand.market, brand.spec, brand.links, brand.salonName, asked]) // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    if (busy) return null
    setBusy(true)
    setError(null)
    hapticTick()
    try {
      const res = await marketingApiService.createCampaign(dispatch, emailDraft(block, { market: brand.market, kit: brand.spec, links: brand.links, salonName: brand.salonName, asked }))
      const cid = res && (res._id || res.id || res.campaignId)
      if (!cid) throw new Error('no id')
      setMemo({ campaignId: cid })
      hapticSuccess()
      setBusy(false)
      return cid
    } catch (e) {
      hapticError()
      setError('The draft couldn’t be saved just now. Nothing was sent.')
    }
    setBusy(false)
    return null
  }
  // "Yes" in the chat saves the draft (never sends: the salon picks who and sends)
  useSaver(id, latest && !campaignId && hasWords, async () => {
    const cid = await save()
    return cid ? `the email "${block.subject || block.heading || 'email'}" saved as a draft in Campaigns (not sent)` : null
  })
  const open = (cid = campaignId) => { hapticTick(); go(CAMPAIGNS_PATH, cid ? { openCampaignId: cid } : { compose: 'email' }) }
  const saveAndOpen = async () => { const cid = await save(); if (cid) open(cid) }

  return (
    <div className={`bw-card is-email${campaignId ? ' is-done' : ''}`} role="group" aria-label={block.title || block.subject || 'Email'}>
      <div className="bw-head">
        <span className="bw-kind"><Mail />Email</span>
        {campaignId && <span className="bw-status is-done"><Tick size={12} />Draft in Campaigns</span>}
      </div>
      {(block.subject || block.preheader) && (
        <div className="bw-subject">
          {block.subject && <p className="bw-title">{block.subject}</p>}
          {block.preheader && <p className="bw-line">{block.preheader}</p>}
        </div>
      )}
      {drawn && drawn.html && (
        <div className={`bw-email${whole ? ' is-whole' : ''}`}>
          <EmailFrame html={drawn.html} width={640} clipHeight={whole ? undefined : (compact ? 260 : 460)} title={`Email: ${block.subject || 'preview'}`} />
          <button type="button" className="bw-email-more" onClick={() => { hapticTick(); setWhole(w => !w) }}>{whole ? 'Show less' : 'See it all'}</button>
        </div>
      )}
      {!brand.isSet && (
        <p className="bw-hint"><Palette size={14} />This uses a guess at your brand. <button type="button" className="bw-link" onClick={() => go(BRAND_PATH)}>Set your brand once</button> and every email wears it.</p>
      )}
      {error && <p className="bw-need" role="alert">{error}</p>}
      <div className="bw-actions">
        {!campaignId && hasWords && (
          <button type="button" className="bw-btn is-secondary" onClick={save} disabled={busy}>{busy ? 'Saving' : 'Save as a draft'}</button>
        )}
        <button type="button" className="bw-btn is-primary" onClick={() => (campaignId ? open() : saveAndOpen())} disabled={busy || (!campaignId && !hasWords)}>
          {campaignId ? 'Open in Campaigns' : 'Save and open'}
        </button>
      </div>
      {!campaignId && <p className="bw-foot">Nothing goes out until you send it from Campaigns.</p>}
    </div>
  )
}

export default EmailWidget
